/**
 * Tab List Component
 *
 * Displays and manages the list of custom tabs (CPT).
 *
 * @since 1.0.0
 */

import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import {
  PostList,
  type PostListItem,
  type PostListStrings,
} from '@redfoxwp/core-components/components/post-list';
import { TabHeader } from '@redfoxwp/core-components/components/tab-header';
import { __ } from '@wordpress/i18n';
import { addQueryArgs } from '@wordpress/url';
import { useMemo, useState } from 'react';

import { useDebounce, useTabs } from '../../hooks';

/**
 * Tab List component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const TabList = (): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Debounce search term with 500ms delay and minimum 3 characters
  const { debouncedValue: debouncedSearchTerm } = useDebounce({
    value: searchTerm,
    delay: 500,
    minLength: 3,
  });

  const { tabs, loading, pagination, deleteTab, goToPage, changeItemsPerPage } = useTabs({
    search: debouncedSearchTerm,
    postsPerPage: 10,
    includeContent: true,
    allStatuses: true,
  });

  // Transform Tab[] to PostListItem[]
  const posts: PostListItem[] = useMemo(
    () =>
      tabs.map((tab) => ({
        id: tab.id,
        title: {
          rendered: tab.title,
        },
        status: tab.status,
        modified: tab.modified,
        date: tab.date,
      })),
    [tabs]
  );

  const handleAddNewTab = (): void => {
    window.location.href = 'post-new.php?post_type=redfoxwp_tab';
  };

  const handleEditTab = (tabId: number): void => {
    window.location.href = `post.php?post=${tabId}&action=edit`;
  };

  const handleDeleteTab = async (tabId: number): Promise<void> => {
    await deleteTab(tabId);
  };

  const handleDuplicateTab = (tabId: number): void => {
    // Get nonce and admin URL from localized script data
    const duplicateNonce = window.redfoxTabManagerSettings?.duplicateNonce ?? '';
    const adminUrl = window.redfoxTabManagerSettings?.adminUrl ?? '/wp-admin/';

    // Navigate to duplicate with nonce
    const duplicateUrl = addQueryArgs(`${adminUrl}post-new.php`, {
      post_type: 'redfoxwp_tab',
      duplicate: tabId.toString(),
      _wpnonce: duplicateNonce,
    });

    window.location.href = duplicateUrl;
  };

  // Localization strings for the PostList component
  const strings: PostListStrings = {
    searchPlaceholder: __('Search tabs…', 'redfox-tab-manager'),
    addNewButton: __('Add New Tab', 'redfox-tab-manager'),
    loadingText: __('Loading…', 'redfox-tab-manager'),
    noPostsFound: __('No tabs found.', 'redfox-tab-manager'),
    noSearchResults: __('Try adjusting your search terms or', 'redfox-tab-manager'),
    clearSearch: __('clear the search', 'redfox-tab-manager'),
    createFirstPost: __('creating your first tab', 'redfox-tab-manager'),
    editLabel: __('Edit', 'redfox-tab-manager'),
    deleteModalTitle: __('Delete Tab', 'redfox-tab-manager'),
    deleteModalMessage: __(
      'Are you sure you want to delete this tab? This action cannot be undone.',
      'redfox-tab-manager'
    ),
    deleteConfirmButton: __('Delete', 'redfox-tab-manager'),
    cancelButton: __('Cancel', 'redfox-tab-manager'),
    modifiedPrefix: __('Modified:', 'redfox-tab-manager'),
    statusLabels: {
      publish: __('Published', 'redfox-tab-manager'),
      draft: __('Draft', 'redfox-tab-manager'),
      pending: __('Pending', 'redfox-tab-manager'),
      private: __('Private', 'redfox-tab-manager'),
      future: __('Scheduled', 'redfox-tab-manager'),
      trash: __('Trash', 'redfox-tab-manager'),
    },
    pagination: {
      noItems: __('No items', 'redfox-tab-manager'),
      oneItem: __('1 item', 'redfox-tab-manager'),
      showing: __('Showing', 'redfox-tab-manager'),
      of: __('of', 'redfox-tab-manager'),
      items: __('items', 'redfox-tab-manager'),
      firstPage: __('First page', 'redfox-tab-manager'),
      goToFirstPage: __('Go to first page', 'redfox-tab-manager'),
      previousPage: __('Previous page', 'redfox-tab-manager'),
      goToPreviousPage: __('Go to previous page', 'redfox-tab-manager'),
      nextPage: __('Next page', 'redfox-tab-manager'),
      goToNextPage: __('Go to next page', 'redfox-tab-manager'),
      lastPage: __('Last page', 'redfox-tab-manager'),
      goToLastPage: __('Go to last page', 'redfox-tab-manager'),
      page: __('Page', 'redfox-tab-manager'),
      goToPage: __('Go to page', 'redfox-tab-manager'),
      itemsPerPage: __('Items per page:', 'redfox-tab-manager'),
    },
    modalStrings: {
      closeModal: __('Close modal', 'redfox-tab-manager'),
    },
  };

  return (
    <Flex direction='column' gap='30px' fullWidth>
      {/* Page header with title and description */}
      <TabHeader
        title={__('Tab List', 'redfox-tab-manager')}
        description={__(
          'Manage individual product tabs. You can create custom tabs that can be assigned to specific products.',
          'redfox-tab-manager'
        )}
      />

      {/* Main content */}
      <Card styleProps={{ padding: '30px', radius: '10px', background: '#fff' }}>
        <PostList
          posts={posts}
          strings={strings}
          loading={loading}
          searchTerm={searchTerm}
          pagination={pagination ?? undefined}
          actions={{ edit: true, delete: true, duplicate: true, preview: false }}
          onSearch={setSearchTerm}
          onAddNew={handleAddNewTab}
          onEdit={handleEditTab}
          onDelete={handleDeleteTab}
          onDuplicate={handleDuplicateTab}
          onPageChange={goToPage}
          onItemsPerPageChange={changeItemsPerPage}
          showItemsPerPageSelector={true}
          itemsPerPageOptions={[10, 20, 50, 100]}
          maxPageButtons={7}
          showPageInfo={true}
          showFirstLastButtons={true}
        />
      </Card>
    </Flex>
  );
};
